/**
===================================================
Module: frontend.src.components.CreateProjectModal.tsx
Description: Modal component for creating new projects
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateProjectRequest } from "../types";

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (projectData: CreateProjectRequest) => void;
  isUploading: boolean;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isUploading,
}) => {
  const { t } = useTranslation('projects');
  const [projectData, setProjectData] = useState<CreateProjectRequest>({
    name: "",
    description: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (projectData.name.trim()) {
      onSubmit(projectData);
      setProjectData({ name: "", description: "" });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ justifyContent: "center" }}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{ 
          height: "auto", 
          maxHeight: "80vh",
          borderRadius: "var(--radius-xl)",
          transform: "none",
          animation: "none" 
        }}
      >
        <div className="modal-header">
          <h2 className="modal-title">{t('createModal.title')}</h2>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">
                {t('createModal.projectName')} <span className="required">{t('createModal.required')}</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={projectData.name}
                onChange={(e) =>
                  setProjectData({ ...projectData, name: e.target.value })
                }
                placeholder={t('createModal.namePlaceholder')}
                required
                disabled={isUploading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('createModal.description')}</label>
              <textarea
                className="form-textarea"
                value={projectData.description}
                onChange={(e) =>
                  setProjectData({
                    ...projectData,
                    description: e.target.value,
                  })
                }
                placeholder={t('createModal.descriptionPlaceholder')}
                rows={3}
                disabled={isUploading}
              />
            </div>
          </form>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isUploading}
            >
              {t('createModal.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={isUploading || !projectData.name.trim()}
            >
              {isUploading ? t('createModal.creating') : t('createModal.createButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
